import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';

const NAV_PAGES = [
  { to: '/leistungen', label: 'Leistungen' },
  { to: '/branchen', label: 'Branchen' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/ueber-uns', label: 'Über uns' },
  { to: '/kontakt', label: 'Kontakt' },
];

export default function MarketingNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}
      data-testid="marketing-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="marketing-logo">
            <img src="/logo.png" alt="LaizyLeads" className="h-8 w-auto" />
            <span className="font-semibold text-lg tracking-tight text-gray-900">LaizyLeads</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {NAV_PAGES.map(item => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.to.replace('/', '')}`}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${isActive(item.to) ? 'text-emerald-700 bg-emerald-50 font-medium' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Anmelden
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20" onClick={() => navigate('/register')} data-testid="nav-register-btn">
              Jetzt registrieren <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>

          <button className="md:hidden text-gray-900 p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-nav-toggle">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 pb-4 px-5">
          {NAV_PAGES.map(item => (
            <Link
              key={item.to}
              to={item.to}
              className={`block w-full text-left py-3 text-sm border-b border-gray-100 ${isActive(item.to) ? 'text-emerald-700 font-medium' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {item.label}
            </Link>
          ))}
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/login')}>Anmelden</Button>
            <Button className="flex-1 bg-emerald-600 text-white" onClick={() => navigate('/register')}>Registrieren</Button>
          </div>
        </div>
      )}
    </nav>
  );
}
